<template>
  <main
    role="main"
    class="col content-spacing"
    style="max-width: 88% !important"
  >
    <section>
      <PageHeader pageTitle="Instalações" style="padding-top: 1rem">
        <div style="display: flex; gap: 0.5rem">
          <GenericSearchInput v-model="search.textSearch" />
          <SelectInstallationStatus v-model="search.installationStatus" />
          <SelectAccountManager v-model="search.accountManagerId" />
          <VueJsonToCsv
            block
            :json-data="installationCSVList"
            :separator="';'"
            :csv-title="'Instalacoes_CSV'"
          >
            <BaseButtonComponent block variant="primary-action">
              CSV
            </BaseButtonComponent>
          </VueJsonToCsv>
        </div>
      </PageHeader>
    </section>

    <InstallationStatusSummaryTable
      v-if="installationSummaryList.length"
      :installationSummaryStatus="installationSummaryList"
    />

    <b-skeleton-table
      animation="wave"
      style="margin-top: 2rem"
      :rows="5"
      :columns="15"
      :table-props="{ bordered: true, striped: true }"
      v-if="isLoading"
    />

    <section class="d-flex justify-content-between flex-column">
      <Table
        class="installation-table"
        v-if="!isLoading && installationList.length"
      >
        <TableHeader>
          <TableHead
            v-for="(installationCol, index) of installationColumns"
            :key="`${installationCol.name}_${index}`"
          >
            <ColumnHeader
              :sort="installationCol.sort"
              :somatory="installationCol.somatory"
              :columnName="installationCol.name"
              :columnKey="installationCol.key"
              :columnType="installationCol.columnType"
              :currentColumnFiltered="currentColumnFiltered"
              :sortDirection="sortDirection"
              :dataList="installationList"
              v-on:sortListByKey="sortListByKey(installationCol.key)"
            />
          </TableHead>
        </TableHeader>
        <TableBody>
          <TableRow
            v-for="(installation, index) of installationList"
            :key="`${installation.installationNumber}_${index}`"
          >
            <TableCell>
              <b-form-input
                debounce="1000"
                v-bind="false"
                v-model="installation.installationNumber"
                style="width: 8rem"
              />
            </TableCell>
            <TableCell>
              <RenderNameColumnValue :name="installation.name" />
            </TableCell>
            <TableCell>
              {{ dateWithHourFormat(installation.currentStatusInitDate) }}
            </TableCell>
            <TableCell>
              {{ installation.daysInStatus }}
            </TableCell>
            <TableCell style="text-align: end">
              <b-form-input
                v-bind="false"
                v-model="installation.lastBillValue"
                style="width: 2 rem"
                placeholder=""
                debounce="1000"
              />
            </TableCell>
            <TableCell>
              <RenderSocialNumberColumnValue
                :socialNumber="installation.socialNumber"
              />
            </TableCell>
            <TableCell>
              <SelectInstallationStatus
                v-model="installation.installationStatus"
              />
            </TableCell>
            <TableCell>
              <b-form-input
                v-bind="false"
                v-model="installation.usage"
                style="width: 2 rem"
                placeholder=""
                debounce="1000"
              />
            </TableCell>
            <TableCell>
              <b-form-input
                v-bind="false"
                v-model="installation.cusd"
                style="width: 2 rem"
                placeholder=""
                debounce="1000"
              />
            </TableCell>
            <TableCell style="text-align: end">
              <RenderMoneyColumnValue
                :value="installation.freeBalance"
                :max-fraction-digits="0"
              />
            </TableCell>
            <TableCell style="min-width: 70px">
              <b-form-input
                v-bind="false"
                v-model="installation.adjustment"
                style="width: 2 rem"
                placeholder=""
                debounce="1000"
              />
            </TableCell>
            <TableCell style="text-align: end">
              <RenderMoneyColumnValue
                :value="installation.injection"
                :max-fraction-digits="0"
              />
            </TableCell>
            <TableCell>
              <b-form-input
                style="min-width: 66px"
                v-model="installation.baseDiscount"
                placeholder=""
                debounce="1000"
              />
            </TableCell>
            <TableCell>
              <div style="display: flex; gap: 1rem;">
                <BaseButtonComponent
                  variant="tertiary-action"
                  size="sm"
                  @click="updateInstallation(installation)"
                >
                  Gravar
                </BaseButtonComponent>
                <BaseButtonComponent
                  v-if="installation.installationStatus === 'IN_ANALYSIS'"
                  variant="danger"
                  size="sm"
                  @click="removeInstallationById(installation.id)"
                >
                  Deletar
                </BaseButtonComponent>
              </div>
            </TableCell>
            <TableCell>
              <RenderNameColumnValue
                v-if="installation.accountManagerName"
                :name="installation.accountManagerName"
              />
            </TableCell>
            <TableCell>
              {{ installation.hasFidelity }}
            </TableCell>
            <TableCell>
              {{ installation.fidelityCounter }}
            </TableCell>
            <TableCell>
              {{ installation.remainingFidelity }}
            </TableCell>
            <TableCell>
              {{ installation.couponName }}
            </TableCell>
            <TableCell>
              <RenderPhoneColumnValue :phoneNumber="installation.phone" />
            </TableCell>
            <TableCell>
              {{ installation.email }}
            </TableCell>
            <TableCell>
              {{ installation.zipCode }}
            </TableCell>
            <TableCell>
              {{ installation.city }}
            </TableCell>
            <TableCell>
              <RenderNameColumnValue :name="installation.district" />
            </TableCell>
            <TableCell>
              {{ installation.address }} {{ installation.number }}
              {{ installation.complement }}
              {{ installation.state }}
            </TableCell>
            <TableCell>
              <div>
                <BaseButtonComponent
                  size="sm"
                  variant="primary-action"
                  @click="openInstallationAddressDialog(installation)"
                >
                  Endereço
                </BaseButtonComponent>
                <BaseButtonComponent
                  size="sm"
                  variant="primary-action"
                  @click="openInstallationOwnerDialog(installation.id)"
                >
                  Trocar Titular
                </BaseButtonComponent>
                <BaseButtonComponent
                  size="sm"
                  variant="primary-action"
                  @click="openInstallationResponsibleDialog(installation)"
                >
                  Info Resp. Cad/Fin
                </BaseButtonComponent>
              </div>
            </TableCell>
            <TableCell>
              <DownloadInstallationBillImageButton
                :disabled="!installation.consumerBillUrl"
                :consumer-bill-url="installation.consumerBillUrl"
              />
            </TableCell>
            <TableCell>
              <b-form-input
                v-bind="false"
                v-model="installation.reasonRefusal"
                style="width: 2 rem"
                placeholder=""
                debounce="1000"
              />
            </TableCell>
            <TableCell>
              <b-form-checkbox
                :checked="Boolean(installation.isCanceled)"
                v-model="installation.isCanceled"
                @change="
                  setCancelledInstallation(
                    Boolean(installation.isCanceled),
                    installation.id,
                  )
                "
                size="sm"
              />
            </TableCell>
            <TableCell>
              <b-form-checkbox
                :checked="Boolean(installation.isOwner)"
                v-model="installation.isOwner"
                @change="
                  setOwnPowerPlaint(
                    Boolean(installation.isOwner),
                    installation.id,
                  )
                "
                size="sm"
              />
            </TableCell>
            <TableCell>
              <RenderDateColumnValue
                style="text-align: end"
                :value="installation.canceledAt"
              />
            </TableCell>
            <TableCell>
              <RenderNameColumnValue
                :name="installation.responsibleNameRegistration"
              />
            </TableCell>
            <TableCell>
              <RenderSocialNumberColumnValue
                :social-number="
                  installation.responsibleSocialNumberRegistration
                "
              />
            </TableCell>
            <TableCell>
              <RenderPhoneColumnValue
                :phone-number="installation.responsiblePhoneRegistration"
              />
            </TableCell>
            <TableCell>
              {{ installation.responsibleEmailRegistration }}
            </TableCell>
            <TableCell>
              {{ installation.responsibleLinkRegistration }}
            </TableCell>
            <TableCell>
              <RenderNameColumnValue
                :name="installation.responsibleNameFinancial"
              />
            </TableCell>
            <TableCell>
              <RenderSocialNumberColumnValue
                :social-number="installation.responsibleSocialNumberFinancial"
              />
            </TableCell>
            <TableCell>
              <RenderPhoneColumnValue
                :phone-number="installation.responsiblePhoneFinancial"
              />
            </TableCell>
            <TableCell>
              {{ installation.responsibleEmailFinancial }}
            </TableCell>
            <TableCell>
              {{ installation.responsibleLinkFinancial }}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <empty-list v-else-if="!isLoading" />
      <AlphabetPagination @letter-selected="fetchDataForLetter" />
    </section>
    <EditionInstallationAddressDialog ref="editModalRef" @getAll="getAll" />
    <ChangeInstallationOwnerDialog ref="changeOwnerModalRef" @getAll="getAll" />
    <EditInstallationResponsibleDialog
      ref="editResponsibleModalRef"
      @getAll="getAll"
    />
  </main>
</template>

<script>
/// <reference path="../../modules/installation/types/index.js" />
/// <reference path="../../modules/common/types/index.js" />

import VueJsonToCsv from 'vue-json-to-csv';
import { GenericSearchInput } from '../../ui/inputs';
import { PageHeader } from '../../ui/page-header';
import {
  SelectInstallationStatus,
  DownloadInstallationBillImageButton,
  EditInstallationResponsibleDialog,
  EditionInstallationAddressDialog,
  ChangeInstallationOwnerDialog,
  InstallationStatusSummaryTable,
} from '../../modules/installation/components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { ColumnHeader } from '../../ui/column-header';
import {
  RenderDateColumnValue,
  RenderMoneyColumnValue,
  RenderNameColumnValue,
  RenderPhoneColumnValue,
  RenderSocialNumberColumnValue,
} from '../../ui/render';
import { AlphabetPagination } from '../../ui/pagination';
import {
  dateWithoutHourFormat,
  dateWithHourFormat,
  defineArrowDirection,
  formatSocialNumber,
  formatPhoneNumber,
  sortByProperty,
} from '../../utils';
import { INSTALLATION_STATUS } from '../../modules/installation/constants';
import helpers from '../../vue/helpers';
import { Api } from '../../vue/api';
import { SelectAccountManager } from '../../ui/selects';
import { BaseButtonComponent } from '../../ui/buttons';
const API = new Api();

export default {
  components: {
    PageHeader,
    GenericSearchInput,
    SelectInstallationStatus,
    Table,
    TableHeader,
    TableHead,
    ColumnHeader,
    TableBody,
    TableRow,
    TableCell,
    RenderNameColumnValue,
    RenderDateColumnValue,
    RenderSocialNumberColumnValue,
    RenderPhoneColumnValue,
    DownloadInstallationBillImageButton,
    EditInstallationResponsibleDialog,
    EditionInstallationAddressDialog,
    ChangeInstallationOwnerDialog,
    InstallationStatusSummaryTable,
    RenderMoneyColumnValue,
    SelectAccountManager,
    AlphabetPagination,
    BaseButtonComponent,
    VueJsonToCsv,
  },
  data() {
    return {
      isLoading: false,
      search: {
        textSearch: null,
        installationStatus: null,
        accountManagerId: null,
        letter: 'A',
      },
      sortDirection: 'ASC',
      currentColumnFiltered: 'daysInStatus',
      /**
       * @type {InstallationAddressForm}
       */
      installationAddressForm: {},
      /**
       * @type {InstallationList[]}
       */
      installationList: [],
      /**
       * @type {InstallationCSVList[]}
       */
      installationCSVList: [],
      /**
       * @type {InstallationSummary[]}
       */
      installationSummaryList: [],
      /**
       * @type {ColumnProperty[]}
       */
      installationColumns: [
        {
          name: 'Instalação',
          key: 'installationNumber',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Nome',
          key: 'name',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Data inicial',
          key: 'currentStatusInitDate',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Dias',
          key: 'daysInStatus',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Valor',
          key: 'lastBillValue',
          columnType: '(R$)',
          sort: true,
          somatory: true,
        },
        {
          name: 'CPF/CNPJ',
          key: 'socialNumber',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Status UC',
          key: 'installationStatus',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Consumo',
          key: 'usage',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Tensão',
          key: 'cusd',
          columnType: '(kWh)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Livre',
          key: 'freeBalance',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Ajuste',
          key: 'adjustment',
          columnType: '(%)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Injeção',
          key: 'injection',
          columnType: '(kWh)',
          sort: true,
          somatory: true,
        },
        {
          name: 'Desc',
          key: 'baseDiscount',
          columnType: '(%)',
          sort: true,
          somatory: false,
        },
        {
          name: 'Gravar',
          key: '',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'CS',
          key: 'accountManagerName',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Fidelidade',
          key: 'hasFidelity',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Fatura',
          key: 'fidelityCounter',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Fid. Restante',
          key: 'remainingFidelity',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Cupom',
          key: 'couponName',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Telefone',
          key: 'phone',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Email',
          key: 'email',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'CEP',
          key: 'zipCode',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Cidade',
          key: 'city',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Bairro',
          key: 'district',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Endereço',
          key: 'address',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Editar',
          key: '',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Imagem',
          key: '',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Observação',
          key: 'reasonRefusal',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Cancelamento',
          key: 'isCanceled',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Usina Própria',
          key: 'isOwner',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Data Cancelamento',
          key: 'canceledAt',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Nome Resp. Cad.',
          key: 'responsibleNameRegistration',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'CPF/CNPJ Resp. Cad.',
          key: 'responsibleSocialNumberRegistration',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Tel. Resp. Cad.',
          key: 'responsiblePhoneRegistration',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Email Resp. Cad.',
          key: 'responsibleEmailRegistration',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Relação Cad.',
          key: 'responsibleLinkRegistration',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Nome Resp. Fin.',
          key: 'responsibleNameFinancial',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'CPF/CNPJ Resp. Fin.',
          key: 'responsibleSocialNumberFinancial',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Tel. Resp. Fin.',
          key: 'responsiblePhoneFinancial',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Email Resp. Fin.',
          key: 'responsibleEmailFinancial',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Relação Fin.',
          key: 'responsibleLinkFinancial',
          columnType: '',
          sort: false,
          somatory: false,
        },
      ],
    };
  },
  async mounted() {
    this.$route.query.letter = this.search.letter;
    const promiseList = await Promise.all([this.getAll()]);
    Promise.resolve(promiseList);
  },
  computed: {
    installationStatus() {
      return this.search.installationStatus;
    },
    textSearch() {
      return this.search.textSearch;
    },
    accountManagerId() {
      return this.search.accountManagerId;
    },
  },
  watch: {
    textSearch() {
      this.handleFilterList('search', 'textSearch');
    },
    installationStatus() {
      this.handleFilterList('status', 'installationStatus');
    },
    accountManagerId() {
      this.handleFilterList('accountManagerId', 'accountManagerId');
    },
  },
  methods: {
    dateWithHourFormat,
    async fetchDataForLetter(letter) {
      this.search.letter = letter;
      await this.handleFilterList('letter', 'letter');
    },
    /**
     * @return {Promise<void>}
     */
    async getAll() {
      this.isLoading = true;
      /** @type {{model: InstallationList[], ok: boolean, errors: Object}} */
      const { model, ok, errors } = await API.post('/execute', {
        path: '/v2/installations' + helpers.toQueryString(this.$route.query),
        method: 'get',
      });

      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }

      this.installationList = model;
      this.installationCSVList = this.parseInstallationListToInstallationCSVList(
        model,
      );
      this.installationSummaryList = this.generateInstallationSummaryList(
        model,
      );
      console.log('this.installationSummaryList', this.installationSummaryList);
      this.$bvToast.toast('Instalações recuperadas com sucesso', {
        title: `Sucesso`,
        variant: 'success',
        solid: true,
      });
      this.isLoading = false;
    },
    /**
     * Gera uma lista instalações no formato CSV
     *
     * @param {InstallationList[]} installationList - Lista de instalações
     * @returns {InstallationCSVList[]} Lista de instalações no formato CSV
     */
    parseInstallationListToInstallationCSVList(installationList) {
      return installationList.map(installation => ({
        Instalacao: installation.installationNumber,
        Nome: installation.name,
        Valor: installation.lastBillValue,
        CPF_CNPJ: formatSocialNumber(installation.socialNumber),
        Rua: installation.addres,
        Bairro: installation.district,
        Estado: installation.state,
        CEP: installation.zipCode,
        Cidade: installation.city,
        Bairro: installation.district,
        Status: INSTALLATION_STATUS[installation.installationStatus],
        Desc: installation.baseDiscount,
        Cupom: installation.couponName,
        Telefone: formatPhoneNumber(installation.phone),
        Email: installation.email,
        Consumo: installation.usage,
        Tensao: installation.cusd,
        Livre: installation.freeBalance,
        Ajuste: installation.adjustment,
        Injecao: installation.injection,
        DataInicialStatus: dateWithHourFormat(
          installation.currentStatusInitDate,
        ),
        TotalDiasNoStatus: installation.daysInStatus,
        Fidelidade: installation.hasFidelity,
        Fatura: installation.fidelityCounter,
        FidRestante: installation.remainingFidelity,
        GerenteDaConta: installation.accountManagerName,
        NomeResponsavelCad: installation.responsibleNameRegistration,
        CPF_CNPJResponsavelCad:
          installation.responsibleSocialNumberRegistration,
        TelefoneResponsavelCad: installation.responsiblePhoneRegistration,
        EmailResponsavelCad: installation.responsibleEmailRegistration,
        RelacaoCad: installation.responsibleLinkRegistration,
        NomeResponsavelFin: installation.responsibleNameFinancial,
        CPF_CNPJResponsavelFin: installation.responsibleSocialNumberFinancial,
        TelefoneResponsavelFin: installation.responsiblePhoneFinancial,
        EmailResponsavelFin: installation.responsibleEmailFinancial,
        RelacaoFin: installation.responsibleLinkFinancial,
      }));
    },
    /**
     * Gera uma lista de resumo das instalações com base em seus status
     *
     * @param {InstallationList[]} installationList - Lista de instalações
     * @returns {InstallationSummary[]} Lista de resumos das instalações
     */
    generateInstallationSummaryList(installationList) {
      const installationSummaryStatusMap = new Map();
      installationSummaryStatusMap.set('TOTAL', {
        name: 'Total',
        total: installationList.length,
        totalInjection: installationList.reduce(
          (total, installation) => total + installation.injection,
          0,
        ),
        percentageOfTotal: 100,
        percentageInjectionOfTotal: 100,
      });

      for (const installationStatus of Object.keys(INSTALLATION_STATUS)) {
        installationSummaryStatusMap.set(installationStatus, {
          name: INSTALLATION_STATUS[installationStatus],
          total: 0,
          totalInjection: 0,
          percentageOfTotal: 0,
          percentageInjectionOfTotal: 0,
        });
      }

      installationList.forEach(installation => {
        const { installationStatus, injection } = installation;
        const installationSummaryStatus = installationSummaryStatusMap.get(
          installationStatus,
        );
        installationSummaryStatus.total += 1;
        installationSummaryStatus.totalInjection += injection;
      });

      const totalOverall = installationSummaryStatusMap.get('TOTAL').total;
      const totalInjectionOverall = installationSummaryStatusMap.get('TOTAL')
        .totalInjection;
      installationSummaryStatusMap.forEach(statusSummary => {
        statusSummary.percentageOfTotal = Math.round(
          (statusSummary.total / totalOverall) * 100,
        );
        statusSummary.percentageInjectionOfTotal = Math.round(
          (statusSummary.totalInjection / totalInjectionOverall) * 100,
        );
      });

      return Array.from(installationSummaryStatusMap.values());
    },
    /**
     * @param {string} columnKey
     * @return {function(): void}
     */
    sortListByKey(columnKey) {
      this.sortDirection = defineArrowDirection(
        this.currentColumnFiltered,
        columnKey,
        this.sortDirection,
      );
      this.currentColumnFiltered = columnKey;
      sortByProperty(this.installationList, columnKey, this.sortDirection);
    },
    /**
     * Método para filtrar a lista
     * @return {function(): void}
     */
    async handleFilterList(queryProp, searchProp) {
      this.$route.query[queryProp] = this.search[searchProp];
      await this.getAll();
    },
    /**
     * Remove uma instalação com base no ID fornecido
     *
     * @param {number} id - ID da instalação a ser removida
     * @returns {Promise<void>}
     */
    async removeInstallationById(id) {
      try {
        this.isLoading = true;

        await API.post('/execute', {
          path: `/installation/remove/${id}`,
          method: 'post',
          data: null,
        });

        this.$bvToast.toast('Instalação removida com sucesso', {
          title: `Sucesso`,
          variant: 'success',
          solid: true,
        });

        await this.getAll();
      } catch (error) {
        this.$bvToast.toast(helpers.getFirstError(error.message), {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Atualiza uma instalação com base nos dados fornecidos
     *
     * @param {InstallationList} installation - Objeto que representa a instalação a ser atualizada
     * @returns {Promise<void>}
     */
    async updateInstallation(installation) {
      try {
        this.isLoading = true;

        const oData = {
          status: installation.installationStatus,
          base_discount: installation.baseDiscount,
          installationNumber: installation.installationNumber,
          usage: installation.usage,
          cusd: installation.cusd,
          adjustment: installation.adjustment,
          reasonRefusal: installation.reasonRefusal,
          lastBillValue: installation.lastBillValue,
        };

        await API.post('/execute', {
          path: `/installation/${installation.id}`,
          method: 'put',
          data: oData,
        });

        this.$bvToast.toast('Instalação alterada com sucesso', {
          title: `Sucesso`,
          variant: 'success',
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast(helpers.getFirstError(error.message), {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Abre um modal e preenche o formulário com os dados da instalação selecionada
     *
     * @param {InstallationList} installation - Objeto contendo os dados da instalação
     */
    openInstallationAddressDialog(installation) {
      this.$refs.editModalRef.openModal({
        id: installation.id,
        city: installation.city,
        state: installation.state,
        address: installation.address,
        district: installation.district,
        number: installation.number,
        zipCode: installation.zipCode,
      });
    },
    /**
     * Abre um modal e preenche o formulário com os dados do novo titular
     *
     * @param {number} installationId - Instalação identificador
     */
    openInstallationOwnerDialog(installationId) {
      this.$refs.changeOwnerModalRef.openModal(installationId);
    },
    /**
     * Abre um modal e preenche o formulário com os dados do responsavel cadastro e financeiro
     *
     * @param {InstallationList} installation - Objeto contendo os dados da instalação
     */
    openInstallationResponsibleDialog(installation) {
      this.$refs.editResponsibleModalRef.openModal({
        id: installation.id,
        responsibleNameRegistration: installation.responsibleNameRegistration,
        responsibleSocialNumberRegistration:
          installation.responsibleSocialNumberRegistration,
        responsiblePhoneRegistration: installation.responsiblePhoneRegistration,
        responsibleEmailRegistration: installation.responsibleEmailRegistration,
        responsibleLinkRegistration: installation.responsibleLinkRegistration,
        responsibleNameFinancial: installation.responsibleNameFinancial,
        responsibleSocialNumberFinancial:
          installation.responsibleSocialNumberFinancial,
        responsiblePhoneFinancial: installation.responsiblePhoneFinancial,
        responsibleEmailFinancial: installation.responsibleEmailFinancial,
        responsibleLinkFinancial: installation.responsibleLinkFinancial,
      });
    },
    /**
     * Marca uma instalação como cancelada
     *
     * @param {boolean} checked - True or False
     * @param {number} id - Instalação identificador
     */
    async setCancelledInstallation(checked, id) {
      console.log({ checked, id });
      try {
        const oData = {
          value: checked,
        };

        await API.post('/execute', {
          path: `/installation/${id}/cancel`,
          method: 'post',
          data: oData,
        });

        this.$bvToast.toast('Instalação alterada com sucesso', {
          title: `Sucesso`,
          variant: 'success',
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast(helpers.getFirstError(error.message), {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
      }
    },
    /**
     * Marca uma instalação como proprietária
     *
     * @param {boolean} checked - True or False
     * @param {number} id - Instalação identificador
     */
    async setOwnPowerPlaint(checked, id) {
      console.log({ checked, id });
      try {
        const oData = {
          value: checked,
        };

        await API.post('/execute', {
          path: `/installation/${id}/owner`,
          method: 'post',
          data: oData,
        });

        this.$bvToast.toast('Instalação alterada com sucesso', {
          title: `Sucesso`,
          variant: 'success',
          solid: true,
        });
      } catch (error) {
        this.$bvToast.toast(helpers.getFirstError(error.message), {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.installation-table {
  display: block;
  max-height: 88vh !important;
  height: auto !important;
  margin-top: 1rem;

  th:first-child,
  td:first-child {
    z-index: 2;
    position: sticky;
    left: 0;
    background-color: #e7e7e7;
  }

  td:nth-child(2),
  th:nth-child(2) {
    z-index: 2;
    position: sticky;
    left: 8.5rem;
    background-color: #e7e7e7;
    min-width: 287px;
  }

  td:nth-child(3),
  th:nth-child(3) {
    z-index: 2;
    position: sticky;
    left: 27rem;
    background-color: #e7e7e7;
  }

  td:nth-child(4),
  th:nth-child(4) {
    z-index: 2;
    position: sticky;
    left: 33.5rem;
    background-color: #e7e7e7;
  }

  td:nth-child(5),
  th:nth-child(5) {
    z-index: 2;
    position: sticky;
    left: 37.5rem;
    background-color: #e7e7e7;
    min-width: 84px;
  }

  td:nth-child(6),
  th:nth-child(6) {
    z-index: 2;
    position: sticky;
    left: 42.5rem;
    background-color: #e7e7e7;
  }

  tr:nth-child(odd) td:nth-child(-n + 6) {
    background-color: #e7e7e7;
  }

  tr:nth-child(even) td:nth-child(-n + 6) {
    background-color: #f2f2f2;
  }
}

.installation-table .money-cell {
  text-align: right;
}
</style>
